import {
  IBudget,
  IMergeTag,
  IMergeTags,
  IStoryContent,
  IStoryMergeTag,
  IStoryMergeTags
} from '@opengov/stories-shared/types';
import { forEach, keys, reduce, values } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { DATA_MERGE_TAG_ID } from '@opengov/stories-shared/utils';

export const MAX_NUMBER_OF_MERGE_TAGS = 50;

export const getMergeTagHtmlId = (mergeTagId: IMergeTag['id']) => `${mergeTagId}_${uuidv4()}`;

export const parseMergeTagId = (
  mergeTagId: IMergeTag['id']
): {
  budgetId: number | null;
  proposalId: number | null;
  fieldId: string | null;
} => {
  try {
    const [budgetId, proposalId, fieldId] = atob(mergeTagId).split(',');
    return { proposalId: parseInt(proposalId, 10), budgetId: parseInt(budgetId, 10), fieldId };
  } catch (error) {
    console.error(error);
    return { proposalId: null, budgetId: null, fieldId: null };
  }
};

export const createMergeTagHtmlId = (mergeTagId: IMergeTag['id']) => `${mergeTagId}_${uuidv4()}`;

export const createMergeTagHtml = (mergeTag: IMergeTag) =>
  `<span
    id="${createMergeTagHtmlId(mergeTag.id)}"
    class="merge-tag noneditable"
    ${DATA_MERGE_TAG_ID}="${mergeTag.id}"
    title="${mergeTag.value}"
  >{<span class="ellipsis">${mergeTag.value}</span>}</span>`;

export const getSortedMergeTagsList = (mergeTags: IMergeTags): IMergeTag[] =>
  values(mergeTags).sort((mergeTagA, mergeTagB) => mergeTagA.label.localeCompare(mergeTagB.label));

export const removeListTagsForSingleMergeTag = (copiedHtml: string): string => {
  const fragment = document.createRange().createContextualFragment(`<div>${copiedHtml}</div>`);
  const container = fragment.children[0] as HTMLDivElement;

  if (
    container?.children.length === 2 &&
    (container.children[1]?.tagName === 'OL' || container.children[1]?.tagName === 'UL') &&
    container.children[1].children?.length === 1
  ) {
    Array.from(container.children[1].children[0].childNodes).forEach((childNode) => container.append(childNode));
    container.children[1].remove();
  }
  return container?.innerHTML || '';
};

export const updateMergeTagHtmlId = (copiedHtml: string): string => {
  const fragment = document.createRange().createContextualFragment(`<div>${copiedHtml}</div>`);
  const mergeTagElements: HTMLSpanElement[] = Array.from(fragment.querySelectorAll('.merge-tag'));

  forEach(mergeTagElements, (mergeTagElement) => {
    const id = mergeTagElement.getAttribute(DATA_MERGE_TAG_ID);
    mergeTagElement.setAttribute('id', createMergeTagHtmlId(id));
  });

  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  return container?.innerHTML || '';
};

export const getStoryMergeTags = (allContents: string, mergeTags: IMergeTags): IStoryMergeTags => {
  const fragment = document.createRange().createContextualFragment(allContents);
  const mergeTagElements: HTMLSpanElement[] = Array.from(fragment.querySelectorAll('.merge-tag'));

  return mergeTagElements.reduce((storyMergeTags, mergeTagElement) => {
    const mergeTagHtmlId = mergeTagElement.getAttribute('id');
    const mergeTagId = mergeTagElement.getAttribute(DATA_MERGE_TAG_ID);
    storyMergeTags[mergeTagHtmlId] = {
      id: mergeTagId,
      htmlId: mergeTagHtmlId,
      value: mergeTagElement.querySelector('.ellipsis')?.textContent || '',
      inactive: !mergeTags[mergeTagId]
    };
    return storyMergeTags;
  }, {} as IStoryMergeTags);
};

const toggleInactiveStoryMergeTag = (mergeTagElement: HTMLSpanElement, inactive = false) => {
  mergeTagElement.classList.toggle('inactive', inactive);

  if (inactive) {
    mergeTagElement.setAttribute('data-hint', 'This field is inactive');
    mergeTagElement.setAttribute('data-hint-position', 'bottom');
    mergeTagElement.setAttribute('data-hint-align', 'center');
  } else {
    mergeTagElement.removeAttribute('data-hint');
    mergeTagElement.removeAttribute('data-hint-position');
    mergeTagElement.removeAttribute('data-hint-align');
  }
};

export interface IProposalsReplacementMap {
  [fromProposalId: number]: { proposalId: number };
}

export enum MergeTagsOperation {
  Remove = 'remove',
  Update = 'update',
  Replace = 'replace'
}

export type StoryMergeTagsRemoveOperation = {
  operation: MergeTagsOperation.Remove;
  onlyInactive?: boolean;
};
export type StoryMergeTagsUpdateOperation = {
  operation: MergeTagsOperation.Update;
  mergeTags: IMergeTags;
};
export type StoryMergeTagsReplaceOperation = {
  operation: MergeTagsOperation.Replace;
  mergeTags: IMergeTags;
  proposalsReplacementMap: IProposalsReplacementMap;
};

export type StoryMergeTagsOperation =
  | StoryMergeTagsRemoveOperation
  | StoryMergeTagsUpdateOperation
  | StoryMergeTagsReplaceOperation;

const removeStoryContentMergeTags = (storyContentContent: string, onlyInactive: boolean): string => {
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  const mergeTagElements: HTMLSpanElement[] = Array.from(fragment.querySelectorAll('.merge-tag'));

  forEach(mergeTagElements, (mergeTagElement) => {
    if (onlyInactive && !mergeTagElement.classList.contains('inactive')) return;
    mergeTagElement.remove();
  });

  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  return container?.outerHTML || '';
};

export const removeStoryMergeTags = (
  storyContents: IStoryContent[],
  { onlyInactive = false }: StoryMergeTagsRemoveOperation
): IStoryContent[] =>
  storyContents.map((storyContent) => ({
    ...storyContent,
    content: removeStoryContentMergeTags(storyContent.content, onlyInactive),
    shouldUpdate: true
  }));

// Track whether any merge tag's active/inactive state actually flipped.
const updateStoryContentMergeTags = (
  storyContentContent: string,
  mergeTags: IMergeTags
): { content: string; changed: boolean } => {
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  const mergeTagElements: HTMLSpanElement[] = Array.from(fragment.querySelectorAll('.merge-tag'));

  let changed = false;
  forEach(mergeTagElements, (mergeTagElement) => {
    const mergeTagId = mergeTagElement.getAttribute(DATA_MERGE_TAG_ID);
    if (mergeTagId) {
      const inactive = !mergeTags[mergeTagId];
      if (mergeTagElement.classList.contains('inactive') !== inactive) changed = true;

      toggleInactiveStoryMergeTag(mergeTagElement, inactive);
    }
  });

  if (!changed) return { content: storyContentContent, changed: false };

  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  return { content: container?.outerHTML ? container.outerHTML : '', changed: true };
};

export const updateStoryMergeTags = (
  storyContents: IStoryContent[],
  { mergeTags }: StoryMergeTagsUpdateOperation
): IStoryContent[] =>
  storyContents.map((storyContent) => {
    const { content, changed } = updateStoryContentMergeTags(storyContent.content, mergeTags);
    // Only set shouldUpdate when merge-tag rendering actually changed.
    return changed ? { ...storyContent, content, shouldUpdate: true } : storyContent;
  });

interface GroupedMergeTags {
  [proposalId: number]: { [fieldId: string]: IMergeTag | IStoryMergeTag };
}

const groupMergeTagsByProposalId = (
  mergeTags: IMergeTags | IStoryMergeTags,
  getIds: (mergeTag: IMergeTag | IStoryMergeTag) => { proposalId: number; fieldId: string }
): GroupedMergeTags => {
  return reduce(
    mergeTags,
    (newMergeTags, mergeTag) => {
      const { proposalId, fieldId } = getIds(mergeTag);
      newMergeTags[proposalId] = {
        ...newMergeTags[proposalId],
        [fieldId]: mergeTag
      };
      return newMergeTags;
    },
    {} as GroupedMergeTags
  );
};

const replaceStoryContentMergeTags = (
  storyContentContent: string,
  mergeTags: IMergeTags,
  groupedMergeTags: GroupedMergeTags,
  proposalsReplacementMap: IProposalsReplacementMap
): string => {
  const fragment = document.createRange().createContextualFragment(storyContentContent);
  const mergeTagElements: HTMLSpanElement[] = Array.from(fragment.querySelectorAll('.merge-tag'));

  forEach(mergeTagElements, (mergeTagElement) => {
    const mergeTagId = mergeTagElement.getAttribute(DATA_MERGE_TAG_ID);

    if (mergeTagId) {
      const { proposalId, fieldId } = parseMergeTagId(mergeTagId);
      const toProposalId = proposalsReplacementMap[proposalId]?.proposalId;
      const mergeTagReplacement = toProposalId && groupedMergeTags[toProposalId][fieldId];
      const inactive = !(mergeTags[mergeTagId] || mergeTagReplacement);

      if (mergeTagReplacement) {
        mergeTagElement.setAttribute('id', createMergeTagHtmlId(mergeTagReplacement.id));
        mergeTagElement.setAttribute(DATA_MERGE_TAG_ID, mergeTagReplacement.id);
        mergeTagElement.setAttribute('title', mergeTagReplacement.value);
        mergeTagElement.querySelector('.ellipsis').textContent = mergeTagReplacement.value;
      }

      toggleInactiveStoryMergeTag(mergeTagElement, inactive);
    }
  });

  const container =
    (fragment.firstElementChild as HTMLElement) ||
    (fragment.firstChild as HTMLElement) ||
    (fragment.children[0] as HTMLElement);
  return container?.outerHTML ? container.outerHTML : '';
};

export const replaceStoryMergeTags = (
  storyContents: IStoryContent[],
  { mergeTags, proposalsReplacementMap }: StoryMergeTagsReplaceOperation
): IStoryContent[] => {
  const groupedMergeTags = groupMergeTagsByProposalId(mergeTags, ({ proposalId, fieldId }: IMergeTag) => ({
    proposalId,
    fieldId
  }));

  return storyContents.map((storyContent) => ({
    ...storyContent,
    content: replaceStoryContentMergeTags(storyContent.content, mergeTags, groupedMergeTags, proposalsReplacementMap),
    shouldUpdate: true
  }));
};

export enum InactiveStoryMergeTagsType {
  BudgetNotFound = 'budgetNotFound',
  ProposalsNotFound = 'proposalsNotFound',
  FieldsNotFound = 'fieldsNotFound'
}

export const checkStoryMergeTags = (
  storyContents: IStoryContent[],
  storyBudget: IBudget = null,
  mergeTags: IMergeTags = {}
): InactiveStoryMergeTagsType | null => {
  const allContents = storyContents.reduce((acc, storyContent) => `${acc}${storyContent.content}`, ``);
  const storyMergeTags = allContents.indexOf(DATA_MERGE_TAG_ID) > -1 ? getStoryMergeTags(allContents, mergeTags) : {};
  const storyMergeTagList = values(storyMergeTags);

  if (storyMergeTagList.length) {
    if (!storyBudget?.proposals.length) return InactiveStoryMergeTagsType.BudgetNotFound;

    const storyMergeTagsHasContent = storyMergeTagList.every(({ id }) => mergeTags[id]);
    if (!storyMergeTagsHasContent) {
      const groupedStoryMergeTags = groupMergeTagsByProposalId(storyMergeTags, ({ id }) => parseMergeTagId(id));

      const storyMergeTagProposalsHasContent = keys(groupedStoryMergeTags).every((proposalId) =>
        values(groupedStoryMergeTags[proposalId]).some(({ id }) => mergeTags[id])
      );
      if (!storyMergeTagProposalsHasContent) {
        return InactiveStoryMergeTagsType.ProposalsNotFound;
      }
      return InactiveStoryMergeTagsType.FieldsNotFound;
    }
  }

  return null;
};
